import { BusinessUserRole } from '../db-models/businesses';
import {
	FCTTitleInsurancePropertyType,
	FCTTitleInsuranceState,
} from '../db-models/title-insurance';

export const fnCallable = {
	getStatus: 'fct_getStatus',
	pushData: 'fct_pushData',
	getRedirectUrl: 'fct_getRedirectUrl',
	getDocument: 'fct_getDocument',
} as const;

export interface BaseFCTRequest {
	lawyerId: string;
	userEmail: string;
	userFirstName: string;
	userRole: BusinessUserRole;
	userLastName: string;
}

export interface GetDocumentRequest extends BaseFCTRequest {
	orderId: string;
	documentName: string;
}
export interface GetDocumentResponse {
	fileFormat: string;
	data: string;
}

export interface GetRedirectUrlRequest extends BaseFCTRequest {
	urlType: RedirectUrlContext;
	dealURN?: string;
}
export interface GetRedirectUrlResponse {
	url: string;
}

export interface FCTSendDataRequest extends BaseFCTRequest {
	data: FCTSendData;
}
export interface FCTSendDataResponse {
	dealUrn: string;
	url: string;
}

export interface FCTgetStatusRequest extends BaseFCTRequest {
	dealURN: string;
}
export interface FCTFee {
	displayName: string;
	amount: number;
	total: number;
	feeType:
		| 'Title Insurance Homeowner'
		| 'Title Insurance Lender'
		| 'Market Value Endorsement'
		| 'Blanket Mortgage'
		| 'Private Lender'
		| 'Cancel Title Insurance';
	nameOfInsured: string;
	totalTaxAmount: number;
	taxes: Tax[];
}
interface Tax {
	name: string;
	amount: number;
}
export interface FCTCredit {
	displayName: string;
	amount: number;
	total: number;
	feeType: 'Reviewing Counsel Fee' | 'Customer Service Credit';
	taxes: Tax[];
}
interface FeesInfo {
	totalFeeAmount: number;
	totalTaxAmount: number;
	totalAmount: number;
	amountDue: number;
	paidAmount: number;
	outstandingAmount: number;
	fees: FCTFee[];
	credits: FCTCredit[];
}

interface Policy {
	policyType: 'Owner' | 'Lender';
	services: string[];
	displayName: string;
	policyNumber: string;
	mortgageNumber: string;
	mortgageSourceId: number;
	lenderName: string;
	ownerName: string;
	insuredAmount: string;
}

interface DealDocument {
	name: string;
	displayName: string;
	documentStatus: 'Requested' | 'Received' | 'Reviewed By FCT';
	statusDisplayName: string;
	statusDateTime: string;
}

interface GeneratedDocument {
	name: string;
	displayName: string;
	generatedDateTime: string;
}

export interface FCTgetStatusResponse {
	orderDate: string;
	status: FCTTitleInsuranceState;
	statusDisplayName: string;
	currentMarketValue: number;
	quoteTimestamp: string;
	feesInfo: FeesInfo;
	quote: FeesInfo;
	policies: Policy[];
	dealDocuments: DealDocument[];
	generatedDocuments: GeneratedDocument[];
}

export type RedirectUrlContext = 'RCONTACTUS' | 'DEAL' | 'USERPROFILE';
export interface LLCDocumentName {
	language: string;
	name: string;
}

export interface FCTPin {
	sourceId: number;
	value: string;
}
export interface FCTParcel {
	estateType: 'Easement' | 'Fee Simple' | 'Leasehold' | 'Other';
	sequence: string;
	legalDescription?: string; // 4000 character limit
	condominiumPlan?: string; //255 character limit
	interest?: number;
}
export interface FCTAddress {
	unitNumber?: string;
	streetNumber?: string;
	streetAddress1: string;
	streetAddress2?: string;
	city?: string;
	province: string;
	postalCode?: string;
}
export interface FCTOwner {
	sourceId: number;
	salutation?: string;
	corporationName?: string;
	firstName?: string;
	lastName?: string;
	dateOfBirth?: string;
	address?: FCTAddress;
	email?: string;
	phone?: string;
}
export interface FCTMortgage {
	sourceId: number;
	primary: boolean;
	referenceNumber?: string;
	amount: number;
	lenderName: string;
	priority: number;
}
export interface FCTSendData {
	dealUrn?: string;
	mmsDeal?: boolean;
	mmsDealUrn?: string;
	matterNumber: string;
	fileNumber: string;
	transactionType: 'Purchase New' | 'Purchase Resale' | 'Mortgage Only';
	purchasePrice?: number;
	closingDate?: string;
	contactFirstName: string;
	contactLastName: string;
	contactFctUserName?: string;
	actingFor?: 'Mortgagor' | 'Mortgagee' | 'Both';
	property: {
		propertyType: FCTTitleInsurancePropertyType;
		occupancy: 'Owner' | 'Rental';
		registration: 'R' | 'LT';
		numberOfUnits?: number;
		address?: FCTAddress;
		pins?: FCTPin[];
		parcels: FCTParcel[];
	};
	owners?: FCTOwner[];
	mortgages: FCTMortgage[];
	vendorSolicitor?: {
		firmName: string;
		firstName: string;
		lastName: string;
	};
	borrowerSolicitor?: {
		firmName: string;
		firstName: string;
		lastName: string;
		phone: string;
	};
	realEstateAgent?: {
		firmName: string;
		firstName: string;
		lastName: string;
		phone: string;
	};
}
